import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useAuth } from '../auth/AuthContext';
import {
  createCheckoutSession,
  createPortalSession,
  fetchSubscription,
  redeemPromoCode,
  SubscriptionSummary,
} from './subscriptionApi';
import {
  BillingInterval,
  formatRenewalDate,
  formatUsd,
  planPriceForInterval,
  SUBSCRIPTION_PLANS,
  yearlySavingsPercent,
} from './subscriptionPlans';

const FREE_PLAN = SUBSCRIPTION_PLANS.find((plan) => plan.id === 'free');
const PRO_PLAN = SUBSCRIPTION_PLANS.find((plan) => plan.id === 'pro');

const PlanFeatureList = ({ features }: { features: string[] }) => (
  <List dense disablePadding>
    {features.map((feature) => (
      <ListItem key={feature} disableGutters sx={{ py: 0.25 }}>
        <ListItemIcon sx={{ minWidth: 32 }}>
          <CheckCircleOutlineIcon color="primary" fontSize="small" />
        </ListItemIcon>
        <ListItemText primary={feature} />
      </ListItem>
    ))}
  </List>
);

const planCardGridSx = {
  display: 'grid',
  gap: 3,
  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
};

const promoSectionVisibleSx = {
  display: { xs: 'block', md: 'none' },
};

const promoSectionAsideSx = {
  display: { xs: 'none', md: 'block' },
  p: 3,
  height: '100%',
};

const planCardSx = {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
};

const planCardColumnWidthSx = {
  width: { xs: '100%', sm: 'calc((100% - 24px) / 2)' },
};

type PromoCodeSectionProps = {
  promoCode: string;
  promoLoading: boolean;
  promoMessage: string;
  promoError: string;
  onPromoCodeChange: (value: string) => void;
  onRedeem: () => void;
};

const PromoCodeSection = ({
  promoCode,
  promoLoading,
  promoMessage,
  promoError,
  onPromoCodeChange,
  onRedeem,
}: PromoCodeSectionProps) => (
  <>
    <Typography variant="h6" gutterBottom>
      Have a promo code?
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Enter a code to extend your free full-access period. Each account can redeem
      one promo code. Codes may have a limited number of uses or an expiration
      date.
    </Typography>
    {promoMessage && (
      <Alert severity="success" sx={{ mb: 2 }}>
        {promoMessage}
      </Alert>
    )}
    {promoError && (
      <Alert severity="error" sx={{ mb: 2 }}>
        {promoError}
      </Alert>
    )}
    <Stack spacing={2}>
      <TextField
        label="Promo code"
        value={promoCode}
        onChange={(event) => onPromoCodeChange(event.target.value)}
        fullWidth
        disabled={promoLoading}
      />
      <Button
        variant="outlined"
        disabled={!promoCode.trim() || promoLoading}
        onClick={onRedeem}
        fullWidth
      >
        {promoLoading ? 'Applying…' : 'Redeem'}
      </Button>
    </Stack>
  </>
);

const SubscriptionPage = () => {
  const { isLoggedIn, isGuest, authChecked } = useAuth();
  const isAuthenticated = isLoggedIn && !isGuest;
  const [searchParams, setSearchParams] = useSearchParams();
  const [billingInterval, setBillingInterval] =
    useState<BillingInterval>('yearly');
  const [subscription, setSubscription] = useState<SubscriptionSummary | null>(
    null,
  );
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoMessage, setPromoMessage] = useState('');
  const [promoError, setPromoError] = useState('');

  const checkoutSuccess = searchParams.get('success') === '1';
  const checkoutCanceled = searchParams.get('canceled') === '1';

  const loadSubscription = useCallback(async () => {
    if (!isAuthenticated) {
      setSubscription(null);
      setLoadingSubscription(false);
      return;
    }

    setLoadingSubscription(true);
    try {
      setSubscription(await fetchSubscription());
    } finally {
      setLoadingSubscription(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authChecked) {
      return;
    }
    void loadSubscription();
  }, [authChecked, loadSubscription]);

  useEffect(() => {
    if (!checkoutSuccess || !isAuthenticated) {
      return;
    }
    const timer = window.setTimeout(() => {
      void loadSubscription();
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [checkoutSuccess, isAuthenticated, loadSubscription]);

  useEffect(() => {
    if (!checkoutSuccess && !checkoutCanceled) {
      return;
    }
    setSearchParams({}, { replace: true });
  }, [checkoutCanceled, checkoutSuccess, setSearchParams]);

  const yearlySavings = PRO_PLAN ? yearlySavingsPercent(PRO_PLAN) : 0;
  const selectedPrice = useMemo(() => {
    if (!PRO_PLAN) {
      return 0;
    }
    return planPriceForInterval(PRO_PLAN, billingInterval);
  }, [billingInterval]);

  const handleSubscribe = async () => {
    if (!PRO_PLAN) {
      return;
    }

    setErrorMessage('');
    setCheckoutLoading(true);
    try {
      const checkoutUrl = await createCheckoutSession(billingInterval);
      window.location.assign(checkoutUrl);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to start checkout',
      );
      setCheckoutLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setErrorMessage('');
    setPortalLoading(true);
    try {
      const portalUrl = await createPortalSession();
      window.location.href = portalUrl;
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Failed to open billing portal',
      );
    } finally {
      setPortalLoading(false);
    }
  };

  const handleRedeemPromo = async () => {
    setPromoError('');
    setPromoMessage('');

    if (!isAuthenticated) {
      setPromoError('Sign in to redeem a promo code.');
      return;
    }

    setPromoLoading(true);
    try {
      const result = await redeemPromoCode(promoCode);
      setSubscription(result.subscription);
      setPromoCode('');
      setPromoMessage(
        `Promo applied. You now have full access until ${formatRenewalDate(result.trialEndsAt) ?? 'the extended date'}.`,
      );
    } catch (error) {
      setPromoError(
        error instanceof Error ? error.message : 'Failed to redeem promo code',
      );
    } finally {
      setPromoLoading(false);
    }
  };

  if (!authChecked) {
    return null;
  }

  const renewalLabel = formatRenewalDate(subscription?.currentPeriodEnd);
  const isProAccount =
    subscription?.isPro === true || subscription?.isLifetimeMember === true;
  const canSubscribe =
    !subscription?.isPro && !subscription?.isLifetimeMember;
  const canManageBilling =
    subscription?.isPro === true && !subscription?.isLifetimeMember;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack spacing={1} sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1">
          Subscription plans
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {canSubscribe
            ? 'Pay securely with Stripe at checkout.'
            : 'Compare what is included on each plan.'}
        </Typography>
      </Stack>

      {checkoutSuccess && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Payment received. Your Pro subscription should activate within a few
          seconds.
        </Alert>
      )}

      {checkoutCanceled && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Checkout was canceled. You can try again whenever you are ready.
        </Alert>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorMessage}
        </Alert>
      )}

      {!isAuthenticated && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <RouterLink to="/login?from=/subscribe" style={{ color: 'inherit' }}>
            Sign in
          </RouterLink>{' '}
          to subscribe.
        </Alert>
      )}

      {isAuthenticated && loadingSubscription && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {isAuthenticated && !loadingSubscription && subscription?.isTrial && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Your first three months are free with full access
          {subscription.trialEndsAt
            ? ` until ${formatRenewalDate(subscription.trialEndsAt)}`
            : ''}
          .
        </Alert>
      )}

      {isAuthenticated && !loadingSubscription && subscription?.freeTier && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Free plan: unlimited puzzles and SRS reviews. Explorer is unlimited.
          Open your first {subscription.freeTier.limits.replayHistoryLimit}{' '}
          replay games anytime; after that, one new replay game per day. Your{' '}
          {subscription.freeTier.limits.replayHistoryLimit} most recent replays
          stay open for repeat viewing.
        </Alert>
      )}

      {isAuthenticated && !loadingSubscription && subscription?.isLifetimeMember && (
        <Alert severity="success" sx={{ mb: 3 }}>
          You have lifetime full access. No subscription required.
        </Alert>
      )}

      {isAuthenticated && !loadingSubscription && subscription?.isPro && (
        <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6" gutterBottom>
                You are on Pro
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {renewalLabel
                  ? `Renews on ${renewalLabel}.`
                  : 'Your subscription is active.'}
              </Typography>
            </Box>
            {canManageBilling && (
              <Button
                variant="outlined"
                disabled={portalLoading}
                onClick={() => void handleManageBilling()}
              >
                {portalLoading ? 'Opening…' : 'Manage billing'}
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      {isAuthenticated && !loadingSubscription && subscription?.isTrial && !subscription.isPro && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You are on a free trial
          {formatRenewalDate(subscription.trialEndsAt)
            ? ` until ${formatRenewalDate(subscription.trialEndsAt)}`
            : ''}
          . Subscribe anytime to keep full access after your trial ends.
        </Alert>
      )}

      {FREE_PLAN && PRO_PLAN && (
        <Stack spacing={3} sx={{ mb: 4 }}>
          {isAuthenticated ? (
            <Box sx={planCardGridSx}>
              <Card variant="outlined" sx={planCardSx}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6">{FREE_PLAN.name}</Typography>
                    {isProAccount && (
                      <Chip label="After trial" size="small" variant="outlined" />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {FREE_PLAN.tagline}
                  </Typography>
                  <Typography variant="h3" component="p" sx={{ mb: 0.5 }}>
                    Free
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    after your trial
                  </Typography>
                  <PlanFeatureList features={FREE_PLAN.features} />
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button fullWidth variant="outlined" disabled>
                    {isProAccount ? 'Included after trial' : 'Default plan'}
                  </Button>
                </CardActions>
                {canSubscribe && (
                  <Box sx={{ ...promoSectionVisibleSx, px: 2, pb: 2 }}>
                    <Divider sx={{ mb: 2 }} />
                    <PromoCodeSection
                      promoCode={promoCode}
                      promoLoading={promoLoading}
                      promoMessage={promoMessage}
                      promoError={promoError}
                      onPromoCodeChange={setPromoCode}
                      onRedeem={() => void handleRedeemPromo()}
                    />
                  </Box>
                )}
              </Card>

              {canSubscribe && (
                <Paper variant="outlined" sx={promoSectionAsideSx}>
                  <PromoCodeSection
                    promoCode={promoCode}
                    promoLoading={promoLoading}
                    promoMessage={promoMessage}
                    promoError={promoError}
                    onPromoCodeChange={setPromoCode}
                    onRedeem={() => void handleRedeemPromo()}
                  />
                </Paper>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Card variant="outlined" sx={{ ...planCardSx, ...planCardColumnWidthSx }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" gutterBottom>
                    {FREE_PLAN.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {FREE_PLAN.tagline}
                  </Typography>
                  <Typography variant="h3" component="p" sx={{ mb: 0.5 }}>
                    Free
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    after your trial
                  </Typography>
                  <PlanFeatureList features={FREE_PLAN.features} />
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button fullWidth variant="outlined" disabled>
                    Default plan
                  </Button>
                </CardActions>
                <Box sx={{ px: 2, pb: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <PromoCodeSection
                    promoCode={promoCode}
                    promoLoading={promoLoading}
                    promoMessage={promoMessage}
                    promoError={promoError}
                    onPromoCodeChange={setPromoCode}
                    onRedeem={() => void handleRedeemPromo()}
                  />
                </Box>
              </Card>
            </Box>
          )}

          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography variant="h5">{PRO_PLAN.name}</Typography>
              <Chip label="Most popular" color="primary" size="small" />
              {subscription?.isLifetimeMember && (
                <Chip label="Your plan" color="primary" size="small" />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {PRO_PLAN.tagline}
            </Typography>
            <Box sx={planCardGridSx}>
              {(
                [
                  {
                    interval: 'monthly' as BillingInterval,
                    label: 'Monthly',
                    priceLabel: 'per month',
                  },
                  {
                    interval: 'yearly' as BillingInterval,
                    label: 'Yearly',
                    priceLabel: 'per year',
                  },
                ] as const
              ).map(({ interval, label, priceLabel }) => {
                const isSelected = billingInterval === interval;
                const price = planPriceForInterval(PRO_PLAN, interval);
                const isCurrentProPlan =
                  subscription?.isPro === true &&
                  subscription.billingInterval === interval;

                return (
                  <Card
                    key={interval}
                    variant="outlined"
                    sx={{
                      ...planCardSx,
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      borderWidth: isSelected ? 2 : 1,
                      position: 'relative',
                    }}
                  >
                    {interval === 'yearly' && yearlySavings > 0 && (
                      <Chip
                        label={`Save ${yearlySavings}%`}
                        color="primary"
                        size="small"
                        sx={{ position: 'absolute', top: 16, right: 16 }}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {label}
                      </Typography>
                      <Typography variant="h3" component="p" sx={{ mb: 0.5 }}>
                        {formatUsd(price)}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {priceLabel}
                      </Typography>
                      <PlanFeatureList features={PRO_PLAN.features} />
                    </CardContent>
                    <CardActions sx={{ px: 2, pb: 2 }}>
                      {subscription?.isLifetimeMember ? (
                        <Button fullWidth variant="outlined" disabled>
                          Lifetime access
                        </Button>
                      ) : isCurrentProPlan ? (
                        <Button fullWidth variant="contained" disabled>
                          Your plan
                        </Button>
                      ) : canSubscribe ? (
                        <Button
                          fullWidth
                          variant={isSelected ? 'contained' : 'outlined'}
                          onClick={() => setBillingInterval(interval)}
                        >
                          {isSelected ? 'Selected' : 'Choose billing'}
                        </Button>
                      ) : (
                        <Button fullWidth variant="outlined" disabled>
                          {formatUsd(price)} {priceLabel}
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                );
              })}
            </Box>
          </Box>
        </Stack>
      )}

      {PRO_PLAN && canSubscribe && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <CreditCardIcon fontSize="small" color="action" />
            <Typography variant="h6">Secure checkout with Stripe</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            You will be redirected to Stripe to enter your payment details.
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                Total due today
              </Typography>
              <Typography variant="h5">
                {formatUsd(selectedPrice)}
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  {billingInterval === 'yearly' ? '/ year' : '/ month'}
                </Typography>
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              disabled={!isAuthenticated || checkoutLoading}
              onClick={() => void handleSubscribe()}
              sx={{ minWidth: { sm: 200 } }}
            >
              {checkoutLoading ? 'Redirecting…' : 'Subscribe with Stripe'}
            </Button>
          </Stack>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: 'block' }}
          >
            Cancel anytime from the billing portal. Subscriptions renew
            automatically until cancelled.
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default SubscriptionPage;
